import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Crown, Car, ArrowRight, Instagram, ExternalLink, Sparkles } from "lucide-react";
import BUSINESS from "../config/business";
import { setViewMode } from "./useIsMobile";
import { haptic, HAPTIC, useTilt } from "./motion";
import MobileEstimateWidget from "./MobileEstimateWidget";
import { api } from "../lib/api";

/**
 * MobileHome — the "How can we help you today?" screen with two premium cards.
 */
export default function MobileHome() {
  const navigate = useNavigate();
  const [specials, setSpecials] = useState([]);

  useEffect(() => {
    api.get("/specials")
      .then((r) => setSpecials(r.data || []))
      .catch(() => setSpecials([]));
  }, []);

  return (
    <div data-testid="mobile-home" className="min-h-screen bg-[#071A2F] text-white pb-28 relative overflow-hidden">
      {/* Ambient gold glow */}
      <div className="absolute -top-40 -left-20 w-96 h-96 rounded-full bg-[#39D6FF]/8 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-20 w-96 h-96 rounded-full bg-[#16B3DD]/8 blur-3xl pointer-events-none" />

      {/* Minimal brand wordmark — replaces the old logo image for the new palette */}
      <motion.header
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="pt-10 pb-6 text-center"
      >
        <div className="text-[10px] tracking-[0.6em] text-[#39D6FF]/70">PLENTY RYDEZ</div>
        <div className="mt-1 text-[8px] tracking-[0.4em] text-white/40">LUXURY GROUND SERVICE</div>
      </motion.header>

      {/* Question */}
      <motion.h1
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="text-center font-serif text-2xl mb-6 px-6"
      >
        How can we help you today?
      </motion.h1>

      {/* Instant estimate widget */}
      <div className="mb-6">
        <MobileEstimateWidget />
      </div>

      {/* Specials — dynamic flyer promos from Back Office */}
      {specials.length > 0 && (
        <div className="px-5 mb-6 space-y-3" data-testid="mobile-specials">
          <div className="label-caps text-[9px] text-cyan flex items-center gap-1.5 pl-1">
            <Sparkles size={10} /> Limited-time specials
          </div>
          {specials.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`mobile-special-${s.id}`}
              onClick={() => { haptic(HAPTIC.select); navigate(`/promo/${s.promo_code}`); }}
              className="block w-full rounded-2xl overflow-hidden border border-white/10 shadow-lg active:scale-[0.98] transition-transform"
            >
              {s.flyer_url ? (
                <img
                  src={s.flyer_url}
                  alt={s.title}
                  className="w-full h-auto block"
                />
              ) : (
                <div className="w-full aspect-[4/3] bg-ink-800 flex items-center justify-center text-white/50">
                  {s.title}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Two premium cards */}
      <div className="px-5 space-y-5">
        <ServiceCard
          testId="mobile-card-chauffeur"
          icon={<Crown size={22} />}
          badge="CHAUFFEUR"
          title="Luxury Chauffeur Services"
          description="Airport Transfers · Corporate · Concerts · Weddings · Proms · Hourly · VIP · Private Events · Night Out"
          cta="ENTER CHAUFFEUR SERVICES"
          delay={0.5}
          onClick={() => { haptic(HAPTIC.select); navigate("/m/chauffeur"); }}
        />
        <ServiceCard
          testId="mobile-card-rental"
          icon={<Car size={22} />}
          badge="RENTAL FLEET"
          title="Rental Fleet"
          description="Daily · Weekly · Monthly · Uber · Lyft · Uber Eats · DoorDash · Personal · Business · Luxury"
          cta="VIEW RENTAL FLEET"
          delay={0.7}
          onClick={() => { haptic(HAPTIC.select); navigate("/m/rentals"); }}
        />
      </div>

      {/* Footer chips */}
      <div className="mt-10 px-5 flex flex-wrap items-center justify-center gap-2 text-[10px] tracking-widest text-white/40">
        <a
          href={BUSINESS.social.instagramUrl}
          target="_blank" rel="noopener noreferrer"
          data-testid="mobile-ig"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 hover:border-[#39D6FF]/40 hover:text-[#39D6FF] transition"
        >
          <Instagram size={11} /> @{BUSINESS.social.instagram}
        </a>
        <button
          type="button"
          onClick={() => setViewMode("desktop")}
          data-testid="mobile-view-desktop"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 hover:border-white/40 hover:text-white/80 transition"
        >
          <ExternalLink size={11} /> View full website
        </button>
      </div>
    </div>
  );
}

function ServiceCard({ testId, icon, badge, title, description, cta, delay, onClick }) {
  const tilt = useTilt({ max: 6 });
  return (
    <motion.button
      type="button"
      data-testid={testId}
      onClick={onClick}
      onPointerMove={tilt.onMove}
      onPointerLeave={tilt.reset}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      style={{
        background: "linear-gradient(145deg, rgba(255,255,255,0.06), rgba(0,0,0,0.4))",
        border: "1px solid rgba(57,214,255,0.35)",
        boxShadow: "0 10px 40px -10px rgba(57,214,255,0.25), inset 0 1px 0 rgba(248,250,252,0.15)",
        backdropFilter: "blur(20px)",
        rotateX: tilt.rotateX,
        rotateY: tilt.rotateY,
        transformPerspective: 900,
        transformStyle: "preserve-3d",
      }}
      className="relative w-full text-left rounded-3xl overflow-hidden group"
    >
      {/* Pointer-tracking glare */}
      <motion.div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background: "radial-gradient(circle 200px at var(--gx) var(--gy), rgba(248,250,252,0.22), transparent 65%)",
          ["--gx"]: tilt.glareX,
          ["--gy"]: tilt.glareY,
        }}
      />
      {/* Static gold sheen */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(circle at 20% 0%, rgba(57,214,255,0.15), transparent 60%)" }}
      />
      <div className="relative p-6" style={{ transform: "translateZ(30px)" }}>
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex items-center gap-1.5 text-[9px] tracking-[0.35em] text-[#39D6FF]">
            <span className="w-6 h-6 rounded-full bg-[#39D6FF]/15 border border-[#39D6FF]/40 flex items-center justify-center text-[#39D6FF]">
              {icon}
            </span>
            {badge}
          </span>
          <ArrowRight size={16} className="text-[#39D6FF] group-hover:translate-x-1 transition-transform" />
        </div>
        <h2 className="font-serif text-2xl leading-tight mb-2 text-white">{title}</h2>
        <p className="text-xs text-white/60 leading-relaxed mb-5">{description}</p>
        <div
          className="w-full py-3 rounded-full text-center text-xs tracking-[0.25em] font-bold text-black transition-all group-active:brightness-90"
          style={{
            background: "linear-gradient(180deg, #F8FAFC 0%, #39D6FF 50%, #39D6FF 100%)",
            boxShadow: "0 4px 20px -4px rgba(57,214,255,0.6), inset 0 1px 0 rgba(255,255,255,0.5)",
          }}
        >
          {cta}
        </div>
      </div>
    </motion.button>
  );
}
